import { cn } from "@/lib/utils"
import homeIcon from "@/assets/hero/hero-icon-home.png"
import youIcon from "@/assets/hero/hero-icon-you.png"
import goalIcon from "@/assets/hero/hero-icon-goal.png"
import abtestIcon from "@/assets/hero/hero-icon-abtest.png"
import connectorsIcon from "@/assets/hero/hero-icon-connectors.png"
import claudeMark from "@/assets/logos/agents/claude.svg"

/**
 * OnboardingStepper: the wizard's progress as a left-to-right bar (team
 * suggestion, 2026-09-10, superseding the stacked receipt). Five segments,
 * each the site's How-it-works marker (mono `01` in a hairline circle), the
 * glossy product icon, the label, and the answer once the step is done, with
 * Edit. The open segment sits on white with a primary underline. Upcoming
 * segments dim the icon the way the LP demo sidebar dims inactive items.
 *
 * Under `sm` the same list renders as a chip strip: number and icon per chip,
 * the label only on the open chip, a teal dot on a done chip whose status is
 * live. The answers are one tap away (the whole chip is the Edit target),
 * which is the trade for keeping the column free on a phone.
 *
 * Semantics: one `nav > ol`, `aria-current="step"` on the open item. State is
 * carried in text and `aria-current`, never by the icon alone.
 */

export type OnboardingStepperIcon = "website" | "you" | "goal" | "tests" | "script" | "agent"

/**
 * There is no glossy agent icon in the pack; the agent step uses the Claude
 * mark the way `McpIntro` shows agents.
 */
const ICONS: Record<OnboardingStepperIcon, string> = {
  website: homeIcon,
  you: youIcon,
  goal: goalIcon,
  tests: abtestIcon,
  script: connectorsIcon,
  agent: claudeMark,
}

const GRID_COLS: Record<number, string> = {
  5: "sm:grid-cols-5",
  6: "sm:grid-cols-6",
}

export type OnboardingStepperStep = {
  id: string
  icon: OnboardingStepperIcon
  label: string
  /** The answer, once the step is done. */
  value?: string
  /** Live status for a done step, announced politely when it changes. */
  status?: { text: string; tone: "teal" | "muted"; pulse?: boolean }
  onEdit?: () => void
}

type OnboardingStepperProps = {
  steps: OnboardingStepperStep[]
  currentId: string
  className?: string
}

export function OnboardingStepper({ steps, currentId, className }: OnboardingStepperProps) {
  const currentIndex = Math.max(0, steps.findIndex((s) => s.id === currentId))

  return (
    <nav aria-label="Setup steps" className={className}>
      <ol
        className={cn(
          "m-0 flex list-none gap-1.5 overflow-x-auto p-0 [scrollbar-width:none]",
          "sm:grid sm:gap-0 sm:overflow-visible sm:border-y sm:border-border/60",
          GRID_COLS[steps.length] ?? "sm:grid-cols-5",
        )}
      >
        {steps.map((step, index) => {
          const done = index < currentIndex
          const active = index === currentIndex
          const tone = active ? "active" : done ? "done" : "upcoming"
          return (
            <li
              key={step.id}
              aria-current={active ? "step" : undefined}
              className={cn(
                // chip (< sm)
                "relative flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-card px-2 py-1 text-xs",
                active && "border-primary/50",
                // segment (sm+)
                "sm:block sm:min-h-[92px] sm:rounded-none sm:border-0 sm:border-r sm:border-border/60 sm:bg-transparent sm:px-3.5 sm:py-3 sm:text-[13px] sm:last:border-r-0",
                active && "sm:bg-card",
                !active && !done && "text-foreground/60",
              )}
            >
              {active ? (
                <span aria-hidden className="absolute inset-x-0 -bottom-px hidden h-0.5 bg-primary sm:block" />
              ) : null}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <StepNumber n={index + 1} tone={tone} />
                <StepIcon icon={step.icon} dim={!active && !done} />
                <span className={cn("sm:hidden", !active && "sr-only")}>{step.label}</span>
                {step.status?.tone === "teal" && done ? (
                  <span aria-hidden className="size-1.5 rounded-full bg-success sm:hidden dark:bg-success" />
                ) : null}
              </div>
              <div className="hidden sm:block">
                <div className="mt-2 text-xs text-muted-foreground">{step.label}</div>
                <div className="truncate font-medium text-foreground">
                  {done ? (
                    (step.value ?? "Done")
                  ) : active ? (
                    <span className="font-normal text-muted-foreground">In progress</span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
                {done && step.status ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className={cn(
                      "mt-0.5 flex items-center gap-1.5 text-[11px]",
                      step.status.tone === "teal" ? "text-success dark:text-success" : "text-muted-foreground",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        step.status.tone === "teal" ? "bg-success dark:bg-success" : "bg-muted-foreground/50",
                        step.status.pulse && "animate-pulse motion-reduce:animate-none",
                      )}
                    />
                    <span className="truncate">{step.status.text}</span>
                  </div>
                ) : null}
              </div>
              {done && step.onEdit ? (
                // One button per step: it covers the whole chip under sm and
                // becomes the small "Edit" text link in the segment corner at sm+.
                <button
                  type="button"
                  onClick={step.onEdit}
                  aria-label={`Edit ${step.label}`}
                  className={cn(
                    "absolute inset-0 rounded-full",
                    "sm:inset-auto sm:right-2 sm:top-2 sm:min-h-6 sm:rounded sm:px-1 sm:text-[11px] sm:text-muted-foreground sm:underline-offset-2 sm:hover:text-foreground sm:hover:underline",
                  )}
                >
                  <span className="sr-only sm:not-sr-only">Edit</span>
                </button>
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** The site's "01 · 02 · 03" step marker: mono numeral in a hairline circle. */
function StepNumber({ n, tone }: { n: number; tone: "active" | "done" | "upcoming" }) {
  return (
    <span
      aria-hidden
      className={cn(
        // 11px mono: every tone measures above 4.5:1 on its own ground.
        "flex size-[22px] shrink-0 items-center justify-center rounded-full border font-mono text-[11px] tabular-nums",
        tone === "active" && "border-primary/40 bg-primary/[0.06] text-[var(--onboarding-action)]",
        tone === "done" && "border-border bg-card text-foreground/75",
        tone === "upcoming" && "border-border/60 text-foreground/70",
      )}
    >
      {String(n).padStart(2, "0")}
    </span>
  )
}

function StepIcon({ icon, dim = false }: { icon: OnboardingStepperIcon; dim?: boolean }) {
  return (
    <span aria-hidden className="flex size-6 shrink-0 items-center justify-center sm:size-7">
      <img
        src={ICONS[icon]}
        alt=""
        draggable={false}
        className={cn(
          "h-full w-full object-contain transition-[opacity,filter] duration-200 motion-reduce:transition-none",
          // A flat brand mark, not a glossy icon: inset so it reads at the same weight.
          icon === "agent" && "p-1",
          dim && "opacity-45 saturate-[0.25]",
        )}
      />
    </span>
  )
}

/** "12s ago", "3m ago", "1h ago"; null when unknown. Pure, for tests. */
export function formatAgo(iso?: string | null, now: number = Date.now()): string | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  const s = Math.max(0, Math.floor((now - t) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  return `${Math.floor(s / 3600)}h ago`
}
